import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Progress, SectionHeader, Typography } from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import RouteKey from '../../../routes/key';
import PersonalDetails from '../../../components/PersonalDetails';
import ContactDetails from '../../../components/ContactDetails';
import SelectedSubjectReview from '../../../components/SelectedSubjectsReview';
import PaymentButton, { PaymentMethod } from '../../payment/components/PaymentButton';
import {
  fallbackEmptyString,
  getNullIfEmptyString,
  parseLocationSearch,
} from '../../../utils/StringUtils';
import { useGetUserDetails } from '../../../api-refactored/user/useGetUserDetails';
import { GET_USER_DETAILS_QUERY_KEY } from '../../../constants/queryKeys';
import { selectIsUserLoggedIn } from '../../../store/appSlice';
import { usePostValidatePaymentToken } from '../../../api-refactored/payment-link/usePostValidatePaymentToken';
import { useGetCandidateProfileAndResit } from '../../../api-refactored/resit-registration/useGetCandidateProfileAndResit';
import { usePostConfirmAndPay } from '../../../api-refactored/resit-registration/usePostConfirmAndPay';
import { ResitSubject } from '../../../api/candidate/resit/model';
import { usePostPaymentAccess } from '../../../api-refactored/financial/usePostPaymentAccess';

const { invalid, receipt, resit, used, expired } = RouteKey['payment-link'].child;
const { paymentLink } = RouteKey.child;

interface ValidateUniqueLinkParams {
  token: string;
}

const useStyles = makeStyles()((theme) =>
({
  subHeaderText: {
    marginBottom: theme.spacing(6),
  },
  pleaseLogoutText: {
    marginBottom: theme.spacing(6),
  },
}));

interface ResitPaymentLinkpParam {
  uuid: string;
}

const ResitPaymentLink = () => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();

  const history = useHistory();
  const { uuid } = useParams<ResitPaymentLinkpParam>();
  const location = useLocation<ValidateUniqueLinkParams>();
  const { isLoading: isLoadingUserDetails } = useGetUserDetails();

  const { token } = parseLocationSearch(location.search);

  const [disabled, setDisabled] = useState(false);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);

  const {
    data: candidateProfileAndResit,
    refetch: getCandidateProfileAndResit,
  } = useGetCandidateProfileAndResit({
    uuid,
    enabled: false,
    onSuccess: () =>
      mutatePostPaymentAccess({
        registrationUuid: uuid,
      }),
  });

  const { mutate: mutatePostPaymentAccess } = usePostPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const invalidSessionCallback = () => {
    getCandidateProfileAndResit();
    queryClient.invalidateQueries(GET_USER_DETAILS_QUERY_KEY);
    return false;
  };

  // Call this API one time to set new cookie.
  // Backend will set cookie to unique link user (note it's not the same as logged out user).
  // In success and fail callbacks, refresh user log in status
  const {
    mutate: invalidateSessionMutate,
    isLoading: isLoadingInvalidateSession,
  } = usePostValidatePaymentToken({
    onSuccess: () => invalidSessionCallback(),
    onError: (err) => {
      const errorCode = err.response?.data.code;

      if (errorCode === '1017') {
        history.push(`/${paymentLink}/${resit}/${used}`);
      } else if (errorCode === '1014') {
        history.push(`/${paymentLink}/${resit}/${expired}`);
      } else if (errorCode === '1019') {
        history.push(`/${paymentLink}/${resit}/${invalid}`);
      }
    },
  });

  const { mutate: postConfirmAndPayMutate } = usePostConfirmAndPay();

  useEffect(() => {
    if (token) {
      invalidateSessionMutate(token);
    } else {
      history.push(`/${paymentLink}/${resit}/${invalid}`);
    }
  }, [token, history, invalidateSessionMutate]);

  if (isUserLoggedIn && token) {
    return (
      <>
        <Typography variant="subtitle1" className={classes.pleaseLogoutText}>
          Authenticated session detected in the current browser. Please log out to proceed.
        </Typography>
        <Button onClick={() => invalidateSessionMutate(token)}>Logout</Button>
      </>
    );
  }

  if (isLoadingUserDetails || isLoadingInvalidateSession || !candidateProfileAndResit) {
    return <Progress color="secondary" />;
  }

  /** ALEX TEST to handle user click payment button
   * */
  const handleClickPaymentButtonTest = (callback: () => void) => {
    callback();
  };

  /** ALEX TEST to handle payment option
   * */
  const handlePaymentOptionTest = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    postConfirmAndPayMutate(
      {
        uuid,
        resitSubjects: {
          subjects: candidateProfileAndResit.subjectSummary.subjectInfo.map(
            (subject) =>
            ({
              subjectCode: subject.nameCode,
            } as ResitSubject)
          ),
          accessLinkToken: getNullIfEmptyString(token),
          paymentMode: fallbackEmptyString(method),
        },
      },
      {
        onSuccess: (registerResponse) => {
          if (registerResponse.paymentSuccess) {
            history.push(
              `/${paymentLink}/${resit}/${receipt}?internalTransactionRef=${registerResponse.internalTransactionRef}`,
              uuid
            );
          }
          callback(registerResponse.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  return (
    <>
      <SectionHeader title="Review your registration details " />
      <Typography variant="body1" className={classes.subHeaderText}>
        Please review your registration details.
      </Typography>

      <PersonalDetails
        statutoryName={candidateProfileAndResit.candidateDetails.statutoryName}
        hanyuPinyinName={candidateProfileAndResit.candidateDetails.hanyuPinyinName}
        nricFin={candidateProfileAndResit.candidateDetails.nricFin}
        nationality={candidateProfileAndResit.candidateDetails.nationality}
        citizenship={candidateProfileAndResit.candidateDetails.citizenship}
        schoolName={candidateProfileAndResit.candidateDetails.schoolName}
      />

      <ContactDetails
        contactDetails={{
          email: candidateProfileAndResit.candidateDetails.email || '',
          mobileNumber: candidateProfileAndResit.candidateDetails.mobileNumber,
          phoneNumber: candidateProfileAndResit.candidateDetails.phoneNumber,
          mailingAddress: candidateProfileAndResit.candidateDetails.mailingAddress,
          foreignAddress1: candidateProfileAndResit.candidateDetails.foreignAddress1,
          foreignAddress2: candidateProfileAndResit.candidateDetails.foreignAddress2,
          foreignAddress3: candidateProfileAndResit.candidateDetails.foreignAddress3,
        }}
        editable={false}
      />

      <SelectedSubjectReview
        examLevel={candidateProfileAndResit.subjectSummary.examLevel}
        examYear={candidateProfileAndResit.subjectSummary.examYear}
        candidateSubjects={candidateProfileAndResit.subjectSummary.subjectInfo}
        gstRegNo={candidateProfileAndResit.subjectSummary.gstRegNo}
        totalExamFee={candidateProfileAndResit.subjectSummary.totalExamFee}
        totalFee={candidateProfileAndResit.subjectSummary.totalFee}
        totalGst={candidateProfileAndResit.subjectSummary.totalGst}
        totalPayable={candidateProfileAndResit.subjectSummary.totalPayable}
        gstAbsorbed={candidateProfileAndResit.subjectSummary.gstAbsorbed}
      />

      <PaymentButton
        isUniqueLink
        disabled={disabled}
        onPaymentComplete={(isRetry) => setDisabled(isRetry)}
        paymentMethodsAvailable={paymentAccessDetailsOption}
        onClickPaymentButton={(callback) => {
          handleClickPaymentButtonTest(callback);
        }}
        onHandlePaymentMethod={(callback, method) => {
          handlePaymentOptionTest(callback, method);
        }}
        onSuccessPayment={(internalTransactionRef) =>
          history.push(
            `/${paymentLink}/${resit}/${receipt}?internalTransactionRef=${internalTransactionRef}`,
            uuid
          )
        }
      />
    </>
  );
};

export default ResitPaymentLink;
